import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  limit,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { CoreFirebaseService } from './CoreFirebaseService';

export interface StagedEntityData {
  id: string;
  entityType: 'description' | 'task' | 'variable' | 'review' | 'rating' | 'bigNumber' | 'studyCase';
  data: Record<string, unknown>;
  accepted: boolean | null;
  source: {
    scrapeResultId: string;
    url: string;
    goal: string;
  };
  createdAt: string;
}

export interface ScrapeAnalysisData {
  id: string;
  status: 'running' | 'completed' | 'reviewed' | 'applied' | 'error';
  mode: 'auto' | 'review';
  goals: string[];
  urls: string[];
  scrapeResultIds: string[];
  summary?: Record<string, unknown>;
  backendTaskId?: string;
  userId: string;
  createdAt: string | { seconds: number; nanoseconds: number };
  updatedAt?: string | { seconds: number; nanoseconds: number };
}

/**
 * ScrapeAnalysisService
 *
 * CRUD for scrape analysis sessions and their staged entities.
 * Path: projects/{projectId}/scrapeAnalyses/{analysisId}
 * Subcollection: extractedEntities/{entityId}
 */
export class ScrapeAnalysisService {
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly projectId: string;

  constructor(projectId: string) {
    this.projectId = projectId;
  }

  private get collectionRef() {
    return collection(this.firestore, 'projects', this.projectId, 'scrapeAnalyses');
  }

  // ── Analysis CRUD ─────────────────────────────────────────────

  async get(id: string): Promise<ScrapeAnalysisData | null> {
    try {
      const docRef = doc(this.firestore, 'projects', this.projectId, 'scrapeAnalyses', id);
      const snap = await getDoc(docRef);
      return snap.exists() ? { ...snap.data(), id: snap.id } as ScrapeAnalysisData : null;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ScrapeAnalysisService.get');
      throw error;
    }
  }

  async list(options: { limit?: number } = {}): Promise<ScrapeAnalysisData[]> {
    try {
      const q = query(this.collectionRef, orderBy('createdAt', 'desc'), limit(options.limit || 20));
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ ...d.data(), id: d.id } as ScrapeAnalysisData));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ScrapeAnalysisService.list');
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      await deleteDoc(doc(this.firestore, 'projects', this.projectId, 'scrapeAnalyses', id));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ScrapeAnalysisService.delete');
      throw error;
    }
  }

  subscribe(onData: (items: ScrapeAnalysisData[]) => void, onError?: (error: Error) => void): Unsubscribe {
    const q = query(this.collectionRef, orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snap) => {
      onData(snap.docs.map(d => ({ ...d.data(), id: d.id } as ScrapeAnalysisData)));
    }, onError);
  }

  /**
   * Subscribe to a single analysis document for real-time status updates.
   */
  subscribeToAnalysis(
    analysisId: string,
    onData: (analysis: ScrapeAnalysisData | null) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const docRef = doc(this.firestore, 'projects', this.projectId, 'scrapeAnalyses', analysisId);
    return onSnapshot(docRef, (snap) => {
      onData(snap.exists() ? { ...snap.data(), id: snap.id } as ScrapeAnalysisData : null);
    }, onError);
  }

  // ── Staged Entities ───────────────────────────────────────────

  private entitiesCollectionRef(analysisId: string) {
    return collection(
      this.firestore, 'projects', this.projectId,
      'scrapeAnalyses', analysisId, 'extractedEntities',
    );
  }

  /**
   * List all staged entities for an analysis.
   */
  async listEntities(analysisId: string): Promise<StagedEntityData[]> {
    try {
      const q = query(this.entitiesCollectionRef(analysisId), orderBy('createdAt', 'asc'));
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ ...d.data(), id: d.id } as StagedEntityData));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ScrapeAnalysisService.listEntities');
      throw error;
    }
  }

  /**
   * List staged entities filtered by type.
   */
  async listEntitiesByType(analysisId: string, entityType: string): Promise<StagedEntityData[]> {
    try {
      const q = query(
        this.entitiesCollectionRef(analysisId),
        where('entityType', '==', entityType),
        orderBy('createdAt', 'asc'),
      );
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ ...d.data(), id: d.id } as StagedEntityData));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ScrapeAnalysisService.listEntitiesByType');
      throw error;
    }
  }

  /**
   * Update the accepted status of a staged entity.
   */
  async updateEntityAccepted(analysisId: string, entityId: string, accepted: boolean): Promise<void> {
    try {
      const docRef = doc(
        this.firestore, 'projects', this.projectId,
        'scrapeAnalyses', analysisId, 'extractedEntities', entityId,
      );
      await updateDoc(docRef, { accepted });
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ScrapeAnalysisService.updateEntityAccepted');
      throw error;
    }
  }

  /**
   * Bulk update accepted status for multiple entities.
   */
  async bulkUpdateAccepted(
    analysisId: string,
    updates: Array<{ entityId: string; accepted: boolean }>,
  ): Promise<void> {
    try {
      const batch = CoreFirebaseService.getInstance().batch();
      for (const { entityId, accepted } of updates) {
        const docRef = doc(
          this.firestore, 'projects', this.projectId,
          'scrapeAnalyses', analysisId, 'extractedEntities', entityId,
        );
        batch.update(docRef, { accepted });
      }
      await batch.commit();
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ScrapeAnalysisService.bulkUpdateAccepted');
      throw error;
    }
  }

  /**
   * Subscribe to staged entities for real-time updates.
   */
  subscribeToEntities(
    analysisId: string,
    onData: (entities: StagedEntityData[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const q = query(this.entitiesCollectionRef(analysisId), orderBy('createdAt', 'asc'));
    return onSnapshot(q, (snap) => {
      onData(snap.docs.map(d => ({ ...d.data(), id: d.id } as StagedEntityData)));
    }, onError);
  }
}
